import { signal } from '@preact/signals';
import type { Locale } from '@gazette/shared';
import { en } from './en.ts';
import { fr, type MessageKey } from './fr.ts';

export type { MessageKey };

const STORAGE_KEY = 'gazette.locale';
const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { fr, en };

function detectLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'fr' || stored === 'en') return stored;
  return navigator.language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

export const locale = signal<Locale>(detectLocale());
document.documentElement.lang = locale.value;

export function setLocale(next: Locale): void {
  locale.value = next;
  localStorage.setItem(STORAGE_KEY, next);
  document.documentElement.lang = next;
}

/** Any object of named values; interfaces such as `Progress` are accepted as-is. */
type Params = object;

function interpolate(template: string, params?: Params): string {
  return params ? template.replace(/\{(\w+)\}/g, (_, key: string) => String((params as Record<string, unknown>)[key] ?? '')) : template;
}

export function t(key: MessageKey, params?: Params, lang: Locale = locale.value): string {
  return interpolate(DICTIONARIES[lang][key], params);
}

type PluralBase = {
  [K in MessageKey]: K extends `${infer Base}.one` ? Base : never;
}[MessageKey];

/** Picks `<base>.one` or `<base>.other`; French treats 0 and 1 as singular. */
export function tn(base: PluralBase, n: number, params?: Params, lang: Locale = locale.value): string {
  const form = new Intl.PluralRules(lang).select(n) === 'one' ? 'one' : 'other';
  return t(`${base}.${form}` as MessageKey, { n, ...params }, lang);
}

/** "Ana", "Ana et Ben", "Ana, Ben et Chloé" */
export function listNames(names: string[], lang: Locale = locale.value): string {
  return new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(names);
}
